import React from 'react';
import { observer } from 'mobx-react';

import { ComponentBase } from '../../../shared/ComponentBase.js';
import { InjectedStyledComponent } from '../../../shared/InjectedStyledComponent.js';
import { ILineProps } from '../../svg/Line/models/index.js';
import { IRectProps } from '../../svg/Rect/models/index.js';
import { ITextProps } from '../../svg/Text/models/index.js';
import Text from '../../svg/Text/Text.js';

import { IBarScaleConfiguration, IBarScaleProps, IBarScaleState } from './models/index.js';
import Bar from './styledComponents/Bar.js';
import Wrapper from './styledComponents/Wrapper.js';
import ZeroAxis from './styledComponents/ZeroAxis.js';
import { BAR_SCALE_CATEGORY_KEY, BAR_SCALE_COMPONENT_KEY } from './BarScale.meta.js';

export class BarScale extends ComponentBase<IBarScaleProps, IBarScaleState, IBarScaleConfiguration> {
	categoryKey = BAR_SCALE_CATEGORY_KEY;
	componentKey = BAR_SCALE_COMPONENT_KEY;

	constructor(props: IBarScaleProps) {
		super(props);
	}

	get container() {
		return this.props.container;
	}

	get label() {
		return this.props.label || { text: '' };
	}

	get rect() {
		return this.props.rect;
	}

	get rectProps(): IRectProps {
		return {
			...this.rect,
			y: this.container.height / 2 - this.rect.height / 2,
			width: Math.abs(this.rect.width),
		};
	}

	get line() {
		return this.props.line;
	}

	get lineProps(): ILineProps {
		return {
			x1: this.line.x,
			x2: this.line.x,
			y1: 0,
			y2: this.container.height,
			...this.line,
		};
	}

	get textProps(): ITextProps {
		let { horizontalAlign = 'edge' } = this.label;
		const { position = 'outside', verticalAlign = 'center', verticalMargin = 0, horizontalMargin = 1 } = this.label;

		const isNegativeBar = this.line.x > this.rect.x || this.line.x === this.container.width;

		const textProps: ITextProps = {
			...this.label.svgTextProperties,
			x: 0,
			y: 0,
		};

		if (verticalAlign === 'center') {
			textProps.y = (this.rectProps.y as number) + this.rect.height / 2;
			textProps.dominantBaseline = 'central';
		} else if (verticalAlign === 'top') {
			textProps.y = (this.rectProps.y as number) + verticalMargin;
			textProps.dominantBaseline = 'hanging';
		} else {
			textProps.y = (this.rectProps.y as number) + this.rect.height - verticalMargin;
			textProps.dominantBaseline = 'text-bottom';
		}

		if (position === 'outside') {
			if (horizontalAlign === 'edge') {
				textProps.x = this.line.x;
				if (isNegativeBar) {
					let width = this.rect.width;
					if (width > 0) {
						width = width * -1;
					}
					textProps.x += width - horizontalMargin;
					textProps.textAnchor = 'end';
				} else {
					textProps.x += this.rect.width + horizontalMargin;
					textProps.textAnchor = 'start';
				}
			} else if (horizontalAlign === 'axis') {
				if (isNegativeBar) {
					textProps.x = 0 + horizontalMargin;
					textProps.textAnchor = 'start';
				} else {
					textProps.x = this.container.width - horizontalMargin;
					textProps.textAnchor = 'end';
				}
			} else {
				textProps.textAnchor = 'middle';
				if (isNegativeBar) {
					textProps.x = this.rect.x / 2;
				} else {
					textProps.x =
						this.line.x + this.rect.width + (this.container.width - this.line.x - this.rect.width) / 2;
				}
			}
		} else {
			if (this.canShowText && this.label.text === '0' && horizontalAlign === 'center') {
				horizontalAlign = 'axis';
			}
			if (horizontalAlign === 'edge') {
				textProps.x = this.line.x;
				if (isNegativeBar) {
					let width = this.rect.width;
					if (width > 0) {
						width = width * -1;
					}
					textProps.x += width + horizontalMargin;
					textProps.textAnchor = 'start';
				} else {
					textProps.x += this.rect.width - horizontalMargin;
					textProps.textAnchor = 'end';
				}
			} else if (horizontalAlign === 'axis') {
				if (isNegativeBar) {
					textProps.x = this.line.x - horizontalMargin;
					textProps.textAnchor = 'end';
				} else {
					textProps.x = this.line.x + horizontalMargin;
					textProps.textAnchor = 'start';
				}
			} else {
				textProps.textAnchor = 'middle';
				if (isNegativeBar) {
					textProps.x = this.rect.x + (this.rectProps.width as number) / 2;
				} else {
					textProps.x = this.line.x + (this.rectProps.width as number) / 2;
				}
			}
		}

		return textProps;
	}

	get canShowText() {
		const { text, hide = false, showZero = false } = this.label;
		if (!hide && typeof text === 'string' && text !== '' && (showZero || text !== '0')) {
			return true;
		}
		return false;
	}

	render() {
		const { className } = this.props;
		const { text } = this.label;

		return (
			<Wrapper
				className={className}
				{...this.container}
				width={this.container.width}
				height={this.container.height}
				aria-label={text}
			>
				<title>{text}</title>
				<Bar {...this.rectProps} />
				<ZeroAxis {...this.lineProps} />
				{this.canShowText && <Text {...this.textProps}>{text}</Text>}
			</Wrapper>
		);
	}
}

export default InjectedStyledComponent(observer(BarScale), BAR_SCALE_CATEGORY_KEY, BAR_SCALE_COMPONENT_KEY);
